import { getNominative } from '@lmc/common/utils'
import {
  type ShiftType as CommonShiftType,
  type VisitType as CommonVisitType,
  type Home,
  type Resident,
  type User,
  UserRole
} from '@lmc/ts-common'
import {
  FilterContainer,
  GroupedResidentsMultiSelect,
  GroupedUsersMultiSelect,
  MultiSelect
} from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Switch } from 'antd'
import useFeatureFlag from 'hooks/useFeatureFlags'
import { capitalize } from 'lodash'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import useResidentsList from 'screens/Rostering/hooks/useResidentsList'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import useUsersList from 'screens/Rostering/hooks/useUsersList'
import { useVisitTypesList } from 'screens/Rostering/hooks/useVisitTypesList'
import {
  type RotaBuilderFiltersType,
  RotaInstanceStatus
} from 'screens/Rostering/types'
import styled from 'styled-components'
import EventIcon from './EventIcon'
import { FilterChips } from './FilterChips'

export const EMPTY_FILTERS: RotaBuilderFiltersType = {
  serviceUsers: [],
  status: [],
  allocations: [],
  warnings: [],
  teamMembers: [],
  typeOfEvent: [],
  roles: [],
  overtime: undefined
}

type FilterOptionKey = 'Type of event' | 'Overtime'

const initialAvailableFiltersResident: FilterOptionKey[] = ['Type of event']

const WARNINGS = [
  'Double booked',
  'Skills not matched',
  'Gender not matched',
  'No warnings'
]

type FilterValue = string[] | boolean

type ActiveFilter = {
  key: string
  value: FilterValue
}

type BuilderFiltersProps = {
  testId?: string
  'data-testid'?: string
  value?: RotaBuilderFiltersType
  onChange?: (value: RotaBuilderFiltersType) => void
  builderType: 'user' | 'resident'
  publishedRota?: boolean
}

export const BuilderFilters = ({
  testId,
  'data-testid': dataTestId,
  value,
  onChange,
  builderType,
  publishedRota
}: BuilderFiltersProps) => {
  const baseId = testId || dataTestId || `builder-filters-${builderType}`
  const [availableFilters, setAvailableFilters] = useState<FilterOptionKey[]>(
    initialAvailableFiltersResident
  )
  const [addedFilters, setAddedFilters] = useState<FilterOptionKey[]>([])
  const [clearTriggered, setClearTriggered] = useState(false)
  const { t } = useTranslation()
  const { shiftTypes } = useShiftTypesList()
  const { visitTypes } = useVisitTypesList()
  const { users } = useUsersList({})
  const { residents } = useResidentsList({})

  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )

  const residentsNominative = getNominative(
    preferredPeopleNominative,
    'Residents'
  )

  const flags = useFeatureFlag({
    flagNames: ['rostering_dev_features', 'rostering_parked_features']
  })?.data

  const flagsRosteringDevFeaturesEnabled =
    flags?.rostering_dev_features?.enabled

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (!value) {
      onChange?.(EMPTY_FILTERS)
    }
  }, [value, onChange])

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (clearTriggered) {
      setClearTriggered(false)
    }
  }, [clearTriggered])

  // unused for now
  // const handleAddFilter: MenuProps['onClick'] = ({ key }) => {
  //   const filterKey = key as FilterOptionKey
  //   setAddedFilters((prev) => [...prev, filterKey])
  //   setAvailableFilters((prev) => prev.filter((filter) => filter !== filterKey))
  // }

  const handleClearAll = () => {
    setAddedFilters([])
    setAvailableFilters(initialAvailableFiltersResident)
    setClearTriggered(true)
    onChange?.(EMPTY_FILTERS)
  }

  const handleInputChange = (newValue: Partial<RotaBuilderFiltersType>) => {
    const updatedValue: RotaBuilderFiltersType = {
      ...(value || {}),
      ...newValue
    }
    onChange?.(updatedValue)
  }

  const handleRemoveFilter = (
    filterKey: keyof RotaBuilderFiltersType,
    valueToRemove: string
  ) => {
    if (!value?.[filterKey]) return

    const newValue = { ...EMPTY_FILTERS, ...value }

    switch (filterKey) {
      case 'overtime':
        if (
          valueToRemove === t('OVERTIME') ||
          valueToRemove === t('NO_OVERTIME')
        ) {
          newValue.overtime = undefined
        }
        break
      case 'status':
        if (Array.isArray(value.status)) {
          newValue.status = value.status.filter(
            (v) => v.toLowerCase() !== valueToRemove.toLowerCase()
          )
        }
        break
      case 'teamMembers':
        if (Array.isArray(value.teamMembers)) {
          const teamMemberId = users?.find(
            (user: User) =>
              `${user.name.first} ${user.name.last}` === valueToRemove
          )?.id
          newValue.teamMembers = value.teamMembers.filter(
            (v) => v !== teamMemberId
          )
        }
        break
      case 'roles':
        if (Array.isArray(value.roles)) {
          newValue.roles = value.roles.filter((v) => v !== valueToRemove)
        }
        break
      case 'serviceUsers':
        if (Array.isArray(value.serviceUsers)) {
          const residentId = residents?.find(
            (resident: Resident) =>
              `${resident.name.first} ${resident.name.last}` === valueToRemove
          )?.id
          newValue.serviceUsers = value.serviceUsers.filter(
            (v) => v !== residentId
          )
        }
        break
      case 'typeOfEvent':
        if (Array.isArray(value.typeOfEvent)) {
          const visitTypeId = visitTypes?.find(
            (visit: CommonVisitType) => visit.name === valueToRemove
          )?.id
          const shiftTypeId = shiftTypes?.find(
            (shift: CommonShiftType) => shift.name === valueToRemove
          )?.id
          const typeId = visitTypeId || shiftTypeId
          newValue.typeOfEvent = value.typeOfEvent.filter((v) => v !== typeId)
        }
        break
      case 'warnings':
      case 'allocations':
      case 'absences':
      case 'flexi':
        if (Array.isArray(value[filterKey])) {
          newValue[filterKey] = value[filterKey]?.filter(
            (v: string) => v !== valueToRemove
          )
        }
        break

      default:
        throw new Error(`Unexpected filter key: ${filterKey}`)
    }

    onChange?.(newValue)
    setClearTriggered(true)
  }

  const getActiveFilters = (): ActiveFilter[] => {
    if (!value) return []
    const entries = Object.entries(value as RotaBuilderFiltersType)
    const filters = entries
      .map(([key, values]): ActiveFilter | null => {
        if (key === 'overtime') {
          if (values === true) {
            return {
              key,
              value: [t('OVERTIME')]
            }
          }
          if (values === false) {
            return {
              key,
              value: [t('NO_OVERTIME')]
            }
          }
          return null
        }

        if (!Array.isArray(values) || !values.length) {
          return null
        }

        switch (key) {
          case 'teamMembers':
            return {
              key,
              value: values.map((v) => {
                const user = users?.find((user: User) => user.id === v)
                return user ? `${user.name.first} ${user.name.last}` : v
              })
            }
          case 'serviceUsers':
            return {
              key,
              value: values.map((v) => {
                const resident = residents?.find(
                  (resident: Resident) => resident.id === v
                )
                return resident
                  ? `${resident.name.first} ${resident.name.last}`
                  : v
              })
            }
          case 'status':
            return {
              key,
              value: values.map((v) => capitalize(v))
            }
          case 'typeOfEvent':
            return {
              key,
              value: values.map((v) => {
                const visitType = visitTypes?.find(
                  (visit: CommonVisitType) => visit.id === v
                )
                const shiftType = shiftTypes?.find(
                  (shift: CommonShiftType) => shift.id === v
                )
                return visitType?.name || shiftType?.name || v
              })
            }
          case 'roles':
            return {
              key,
              value: values.map((v) => capitalize(v))
            }
          default:
            return {
              key,
              value: values
            }
        }
      })
      .filter((filter): filter is ActiveFilter => filter !== null)
    return filters
  }

  const dropdownItems = availableFilters.map((filter) => ({
    key: filter,
    label: filter
  }))

  const instanceStatusOptions = Object.values(RotaInstanceStatus).map(
    (value) => ({
      label: capitalize(value),
      value,
      image: <EventIcon status={value} />
    })
  )

  const warningOptions = WARNINGS.map((item) => ({
    label: item,
    value: item
  }))

  const allocationOptions = [
    { value: 'Unallocated', label: t('UNALLOCATED') },
    { value: 'Partially Allocated', label: t('PARTIALLY_ALLOCATED') },
    { value: 'Allocated', label: t('FULLY_ALLOCATED') }
  ]

  const roleOptions = [
    { label: 'Carer', value: UserRole.carer },
    { label: 'Senior Carer', value: UserRole.seniorCarer },
    { label: 'Admin', value: UserRole.careHomeAdmin }
  ]

  const visitOptions =
    visitTypes?.map((visit: CommonVisitType) => ({
      label: `${visit.name} Visit`,
      value: visit.id
    })) || []

  const shiftOptions =
    shiftTypes?.map((shift: CommonShiftType) => ({
      label: `${shift.name} Shift`,
      value: shift.id
    })) || []

  const overtimeOptions = [
    { label: t('OVERTIME'), value: 'overtime' },
    { label: t('NO_OVERTIME'), value: 'no_overtime' }
  ]

  // Combine visit and shift options into a flat array
  const eventOptions = [...visitOptions, ...shiftOptions]

  const showFilterChips =
    Object.values(value || {}).some(
      (arr) => Array.isArray(arr) && arr.length > 0
    ) || value?.overtime !== undefined

  return (
    <FilterContainer title={t('FILTERS')} testId={baseId}>
      <>
        <Filters>
          <LeftContainer>
            <GroupedResidentsMultiSelect
              key={clearTriggered ? `residents-clear` : `residents`}
              residents={residents}
              title={residentsNominative}
              values={value?.serviceUsers}
              handleInputChange={(selectedOptions) =>
                handleInputChange({
                  serviceUsers: selectedOptions as string[]
                })
              }
            />
            <StyledMultiSelect
              key={clearTriggered ? `status-clear` : `status`}
              title={t('EVENT_STATUS')}
              options={
                !publishedRota
                  ? instanceStatusOptions
                  : instanceStatusOptions.filter(
                      (status) =>
                        status.value !== RotaInstanceStatus.DRAFT &&
                        status.value !== RotaInstanceStatus.CANCELLED
                    )
              }
              values={value?.status}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedStatus) =>
                handleInputChange({
                  status: selectedStatus as RotaInstanceStatus[]
                })
              }
            />
            <StyledMultiSelect
              key={clearTriggered ? `allocations-clear` : `allocations`}
              values={value?.allocations}
              title={t('ALLOCATIONS')}
              options={allocationOptions}
              hasClear
              hasSearch
              hasSelectAll
              onChange={(selectedValues) =>
                handleInputChange({
                  allocations: selectedValues as string[]
                })
              }
            />
            {flagsRosteringParkedFeaturesEnabled && (
              <StyledWideMultiSelect
                key={clearTriggered ? `warnings-clear` : `warnings`}
                values={value?.warnings}
                title={t('WARNINGS')}
                options={warningOptions}
                hasClear
                hasSearch
                hasSelectAll
                onChange={(selectedValues) =>
                  handleInputChange({
                    warnings: selectedValues as string[]
                  })
                }
              />
            )}
            <GroupedUsersMultiSelect
              key={clearTriggered ? `team-members-clear` : `team-members`}
              users={users}
              title={t('TEAM_MEMBERS')}
              values={value?.teamMembers}
              handleInputChange={(selectedOptions) =>
                handleInputChange({
                  teamMembers: selectedOptions as string[]
                })
              }
            />
            {builderType === 'user' && (
              <>
                <StyledMultiSelect
                  key={clearTriggered ? `roles-clear` : `roles`}
                  values={value?.roles}
                  title={t('ROLES')}
                  options={roleOptions}
                  hasClear
                  hasSearch
                  hasSelectAll
                  onChange={(selectedValues) =>
                    handleInputChange({
                      roles: selectedValues as UserRole[]
                    })
                  }
                />
                <StyledMultiSelect
                  key={clearTriggered ? `overtime-clear` : `overtime`}
                  values={
                    value?.overtime === true
                      ? ['overtime']
                      : value?.overtime === false
                        ? ['no_overtime']
                        : undefined
                  }
                  title={t('OVERTIME')}
                  options={overtimeOptions}
                  hasClear
                  onChange={(selectedValues) => {
                    handleInputChange({
                      overtime: selectedValues?.includes('overtime')
                        ? true
                        : selectedValues?.includes('no_overtime')
                          ? false
                          : undefined
                    })
                  }}
                />
              </>
            )}
            {/* {addedFilters.includes('Type of event') && ( */}
            <StyledMultiSelect
              key={clearTriggered ? `type-of-event-clear` : `type-of-event`}
              values={value?.typeOfEvent}
              title={t('TYPE_OF_EVENT')}
              options={eventOptions}
              hasClear
              hasSelectAll
              onChange={(selectedValues) =>
                handleInputChange({
                  typeOfEvent: selectedValues as string[]
                })
              }
            />
            {/* )} */}
            {/* {addedFilters.length !== initialAvailableFiltersResident.length && (
              <ButtonContainer>
                <Dropdown
                  menu={{ items: dropdownItems, onClick: handleAddFilter }}
                  trigger={['click']}
                >
                  <Button variant="text">
                    <Add /> {t('ADD_FILTER')}
                  </Button>
                </Dropdown>
              </ButtonContainer>
            )} */}
          </LeftContainer>
          {flagsRosteringDevFeaturesEnabled && (
            <RightContainer>
              <SpotlightText weight="medium">{t('SPOTLIGHT')}</SpotlightText>
              <Switch />
            </RightContainer>
          )}
        </Filters>
        {showFilterChips && (
          <FilterChips
            filters={getActiveFilters()}
            onRemove={handleRemoveFilter}
            onClearAll={handleClearAll}
          />
        )}
      </>
    </FilterContainer>
  )
}

const LeftContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`

const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledMultiSelect = styled(MultiSelect)`
  min-width: 160px;
`

const StyledWideMultiSelect = styled(MultiSelect)`
  min-width: 200px;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
`

const Filters = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
`

const SpotlightText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`
